import type { ExpenditureBudgetPurposeModel } from '@/features/master-table/models';
import type { ExpenditureCartsModel } from '@/features/project/projects/_models/expenditure-cart';
import type { ExpenditureItemsModel } from '@/features/project/projects/_models/expenditure-item';
import type { IndonesiaData } from '@/features/project/projects/_models/indonesia-data';
import type { SubProjectsModel } from '@/features/project/projects/_models/sub-project';
import { BigCalculator, NEST_ITEM_CONNECTOR } from '@/utils';
import dayjs from 'dayjs';
import type { z } from 'zod';
import { convertBudgetTypeNameToOrderingKey } from './review-sheet/budget-and-execution';

const getExpenditureBudgetPurposeName = (
  data: z.infer<typeof ExpenditureBudgetPurposeModel>,
) => {
  return [
    data.accountingType.name,
    data.jurisdiction.name,
    data.accountingType.type === 'general'
      ? data.organization?.name
      : data.accountTitle?.name,
    data.expenditurePurpose.upper?.name,
    data.expenditurePurpose.name,
  ]
    .filter(Boolean)
    .join(NEST_ITEM_CONNECTOR);
};

export const getExpenditureBudgetPurposeTableName = (
  data: z.infer<typeof ExpenditureBudgetPurposeModel> | null,
  expenditureBudgetPurposeDescription: string | null | undefined,
) => {
  return data
    ? getExpenditureBudgetPurposeName(data)
    : expenditureBudgetPurposeDescription;
};

export const sortByName = (
  data: IndonesiaData['belanjaProgram'] | IndonesiaData['biayaTenaga'],
) => {
  return data?.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
};

export const sortBelanjaProgramData = (
  data: IndonesiaData['belanjaProgram'],
) => {
  if (!data) {
    return [];
  }

  // Step 1: 親要素だけを抽出してcreatedAtでソート
  const parents = data
    .filter((item) => item.parentId === null || item.parentId === undefined)
    .sort((a, b) => a.createdAt?.localeCompare(b.createdAt ?? '') ?? 0);

  // Step 2: 各親に対して子要素をグループ化
  const childrenByParentId = data
    .filter((item) => item.parentId !== null && item.parentId !== undefined)
    .reduce(
      (acc, child) => {
        if (!child.parentId) {
          return acc;
        }
        if (!acc[child.parentId]) {
          acc[child.parentId] = [];
        }
        acc[child.parentId]?.push(child);
        return acc;
      },
      {} as Record<string, typeof data>,
    );

  // Step 3: 各親の子要素をchildIndexでソート
  // biome-ignore lint/complexity/noForEach: <explanation>
  Object.keys(childrenByParentId).forEach((parentId) => {
    childrenByParentId[parentId]?.sort(
      (a, b) => (a.childIndex ?? 0) - (b.childIndex ?? 0),
    );
  });

  // Step 4: 親と子を正しい順序で結合
  const result: typeof data = [];
  // biome-ignore lint/complexity/noForEach: <explanation>
  parents.forEach((parent) => {
    // 親を追加
    result.push(parent);
    // その親の子供たちを追加
    const children = childrenByParentId[parent.id] || [];
    result.push(...children);
  });

  return result;
};

export const sortExpenditureItemsData = (
  data: z.infer<typeof ExpenditureItemsModel>,
) => {
  return data.sort((a, b) => {
    const budgetTypeOrder =
      convertBudgetTypeNameToOrderingKey(a.budgetType.name) -
      convertBudgetTypeNameToOrderingKey(b.budgetType.name);
    if (budgetTypeOrder !== 0) {
      return budgetTypeOrder;
    }

    const descendingOrderUpdatedAt = dayjs(b.updatedAt).diff(
      dayjs(a.updatedAt),
    );

    const aHasDescription = !!a.expenditureBudgetPurposeDescription;
    const bHasDescription = !!b.expenditureBudgetPurposeDescription;
    if (aHasDescription && !bHasDescription) {
      return 1;
    }
    if (!aHasDescription && bHasDescription) {
      return -1;
    }

    const aFinalizedAmount = a.finalizedAmount ?? 0;
    const bFinalizedAmount = b.finalizedAmount ?? 0;
    if (
      !(aHasDescription && bHasDescription) &&
      bFinalizedAmount !== aFinalizedAmount
    ) {
      return BigCalculator.subtract(bFinalizedAmount, aFinalizedAmount);
    }
    return descendingOrderUpdatedAt;
  });
};

// To make variable's type optional
export const getGarageBySubProjects = (
  subProjects: z.infer<typeof SubProjectsModel>,
) => subProjects.at(0);

type ExpenditureCartsType = z.infer<typeof ExpenditureCartsModel>;

export const splitExpenditureCartsByAccountingType = (
  data: ExpenditureCartsType,
) => {
  const { general, special } = data.reduce<{
    general: ExpenditureCartsType;
    special: ExpenditureCartsType;
  }>(
    (acc, cart) => {
      switch (cart.accountingType.type) {
        case 'general':
          acc.general.push(cart);
          break;
        case 'special':
          acc.special.push(cart);
          break;
      }
      return acc;
    },
    { general: [], special: [] },
  );

  return [general, special].filter((v) => v.length > 0);
};
